import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../../firebaseConfig';
import { INTELLIGENCE_LIMITS, intelligenceFileType, intelligenceFiles, intelligenceInput } from '../../functions/src/common/intelligenceAnalysis';
import { intelligenceAnalysisStyles as styles } from '../common/intelligenceAnalysisStyles';

interface Props { organizationId: string; onClose: () => void; onOpenReport: (reportId: string) => void }
type Job = { status: string; stage: string; error?: string; startedAtMs?: number; reportId?: string };
const callable = httpsCallable(functions, 'manageIntelligenceAnalysis', { timeout: 60000 });

function storedJob(key: string) {
    try { return sessionStorage.getItem(key) || ''; }
    catch { return ''; }
}

const AnalyzeIntelligenceModal: React.FC<Props> = ({ organizationId, onClose, onOpenReport }) => {
    const dialog = useRef<HTMLDialogElement>(null);
    const closeButton = useRef<HTMLButtonElement>(null);
    const openedReport = useRef('');
    const storageKey = 'intelligence-analysis:' + organizationId + ':' + (auth.currentUser?.uid || '');
    const [jobId, setJobId] = useState(() => storedJob(storageKey));
    const [job, setJob] = useState<Job | null>(null);
    const [url, setUrl] = useState('');
    const [text, setText] = useState('');
    const [questions, setQuestions] = useState('');
    const [context, setContext] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [uploadStage, setUploadStage] = useState('');
    const [now, setNow] = useState(Date.now());
    const pending = busy || !!jobId && (!job || ['draft', 'queued', 'processing'].includes(job.status));
    const overdue = !!job?.startedAtMs && now - job.startedAtMs > INTELLIGENCE_LIMITS.runMs && ['queued', 'processing'].includes(job.status);

    const remember = (id: string) => {
        try {
            if (id)
                sessionStorage.setItem(storageKey, id);
            else
                sessionStorage.removeItem(storageKey);
        } catch { /* The background job also saves to the library. */ }
        setJobId(id);
    };

    useEffect(() => {
        const target = dialog.current;
        const previous = document.activeElement as HTMLElement | null;
        target?.showModal();
        return () => { target?.close(); previous?.focus(); };
    }, []);

    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 10000);
        return () => window.clearInterval(timer);
    }, []);

    useEffect(() => {
        if (!jobId)
            return;
        return onSnapshot(doc(db, 'intelligence_analysis_jobs', jobId), snapshot => {
            if (!snapshot.exists()) {
                setError('This analysis session expired. Start a new analysis.');
                setJob({ status: 'failed', stage: 'Failed' });
                return;
            }
            const value = snapshot.data() as Job;
            setJob(value);
            if (value.status === 'complete' && value.reportId && openedReport.current !== value.reportId) {
                openedReport.current = value.reportId;
                try { sessionStorage.removeItem(storageKey); } catch { /* Storage is optional. */ }
                onOpenReport(value.reportId);
            }
        }, () => {
            setError('Analysis progress could not be loaded. Reopen this window to reconnect.');
            setJob({ status: 'failed', stage: 'Connection lost' });
        });
    }, [jobId, storageKey, onOpenReport]);

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (pending)
            return;
        setError('');
        let preparedId = '';
        let started = false;
        try {
            const descriptors = files.map(file => ({ name: file.name, size: file.size, contentType: intelligenceFileType(file.name) }));
            intelligenceFiles(descriptors);
            const input = intelligenceInput({ url, text, questions, context }, files.length);
            setBusy(true);
            setUploadStage('Preparing analysis');
            const result = await callable({ action: 'prepare', organizationId, files: descriptors });
            const prepared = result.data as { jobId: string; uploads: { uploadUrl: string; contentType: string }[] };
            preparedId = prepared.jobId;
            for (let index = 0; index < files.length; index++) {
                setUploadStage('Uploading document ' + (index + 1) + ' of ' + files.length);
                const response = await fetch(prepared.uploads[index].uploadUrl, { method: 'PUT',
                    headers: { 'Content-Type': prepared.uploads[index].contentType }, body: files[index], signal: AbortSignal.timeout(120000) });
                if (!response.ok)
                    throw new Error('Document upload failed. Please try again.');
            }
            setUploadStage('Starting analysis');
            // Keep the durable job ID even if the start response is lost.
            remember(preparedId);
            started = true;
            await callable({ action: 'start', organizationId, jobId: preparedId, input });
        } catch (failure) {
            setError(failure instanceof Error ? failure.message : 'Analysis could not start.');
            if (preparedId && !started) {
                void callable({ action: 'discard', organizationId, jobId: preparedId }).catch(() => {});
                remember('');
            }
            if (started)
                setJob(current => current?.status === 'complete' ? current : { status: 'failed', stage: 'Start not confirmed' });
        } finally {
            setBusy(false);
            setUploadStage('');
        }
    };

    return createPortal(<dialog ref={dialog} aria-labelledby="analyze-intelligence-title"
        className={[styles.panel, 'm-auto max-h-[90dvh] w-[calc(100%_-_2rem)] max-w-2xl overflow-y-auto backdrop:bg-slate-950/70'].join(' ')}
        onCancel={event => { event.preventDefault(); if (!busy) onClose(); }}>
        <header className="mb-6 flex items-start justify-between gap-4 border-b border-blue-100 pb-5">
            <div><p className="mb-2 text-xs font-bold uppercase tracking-widest text-blue-600">Self-service intelligence</p>
                <h2 id="analyze-intelligence-title" className="text-2xl font-extrabold">Analyze Intelligence</h2>
                <p className={styles.muted}>Turn a story or document into a focused intelligence brief.</p></div>
            <button ref={closeButton} type="button" className={styles.secondary} onClick={onClose} disabled={busy} aria-label="Close analysis">×</button>
        </header>
        {error && <p role="alert" className="mb-4 rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-800">{error}</p>}
        {jobId && !busy ? <div className="space-y-5">
            <div role="status" aria-live="polite" className="rounded-xl border border-blue-200 bg-blue-50 p-5">
                <p className="font-bold">{overdue ? 'This analysis is taking longer than expected.' : job?.status === 'failed' ? 'Analysis could not finish' : job?.stage || 'Connecting to analysis...'}</p>
                <p className={[styles.muted, 'mt-2'].join(' ')}>{job?.error || (overdue
                    ? 'Check your Intelligence Library before starting another run.'
                    : 'You can close this window. Your finished report will appear in Intelligence Library.')}</p>
                {pending && !overdue && <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-blue-200"><div className="h-full w-1/3 animate-pulse rounded-full bg-blue-600" /></div>}
            </div>
            <div className="flex flex-wrap justify-end gap-3">
                {(job?.status === 'failed' || overdue) && <button type="button" className={styles.secondary}
                    onClick={() => { remember(''); setJob(null); setError(''); }}>New analysis</button>}
                <button type="button" className={styles.secondary} onClick={onClose}>Close window</button>
            </div>
        </div> : <form onSubmit={event => void submit(event)} className="space-y-5">
            <p className={styles.muted}>Add at least one link, text entry, or document.</p>
            <label className={styles.label}>Article link<input type="url" value={url} onChange={event => setUrl(event.target.value)}
                maxLength={2048} placeholder="https://" disabled={busy} className={styles.field} /></label>
            <label className={styles.label}>Intelligence text<textarea value={text} onChange={event => setText(event.target.value)}
                maxLength={INTELLIGENCE_LIMITS.text} rows={5} placeholder="Paste the intelligence you want assessed" disabled={busy} className={styles.field} /></label>
            <label className={styles.label}>Documents<input type="file" multiple accept=".pdf,.docx,.pptx,.ppsx,.txt" disabled={busy}
                className={[styles.field, 'file:mr-3 file:rounded-lg file:border-0 file:bg-blue-50 file:px-3 file:py-2 file:text-sm file:font-bold file:text-blue-700'].join(' ')}
                aria-describedby="intelligence-upload-help" onChange={event => { setFiles(Array.from(event.target.files || [])); setError(''); }} />
                <span id="intelligence-upload-help" className={styles.muted}>PDF, DOCX, PPTX, PPSX, or TXT. Up to 3 files, 25MB each.</span></label>
            <label className={styles.label}>Key questions <span className="font-normal">(optional)</span><textarea value={questions}
                onChange={event => setQuestions(event.target.value)} rows={2} maxLength={INTELLIGENCE_LIMITS.questions} disabled={busy} className={styles.field} /></label>
            <label className={styles.label}>Sites and business context <span className="font-normal">(optional)</span><textarea value={context}
                onChange={event => setContext(event.target.value)} rows={2} maxLength={INTELLIGENCE_LIMITS.context} disabled={busy} className={styles.field}
                placeholder="Locations, operations, or decisions this intelligence affects" /></label>
            <p className={styles.muted}>Your brief includes source checks, business impact, and recommended actions. It saves automatically to your organization’s Intelligence Library.</p>
            <footer className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 pt-5">
                <span role="status" aria-live="polite" className={styles.muted}>{uploadStage}</span>
                <button type="button" disabled={busy} className={styles.secondary} onClick={onClose}>Cancel</button>
                <button type="submit" disabled={busy} className={styles.primary}>{busy ? 'Preparing...' : 'Generate report'}</button>
            </footer>
        </form>}
    </dialog>, document.body);
};

export default AnalyzeIntelligenceModal;
